/* eslint-disable react/jsx-props-no-spreading */
import React, { lazy, Suspense } from 'react';
import { Redirect, Route, Switch } from 'react-router-dom';
import { LicenseManager } from 'ag-grid-enterprise';
import styled from 'styled-components';
import { Loader, useComponentHeight } from '@mummssoftware/common-ui';
import { FAJITA } from '@mummssoftware/utils/constants';
import { FajitaIntro } from '../FajitaForms/FajitaIntro';
import { FormsExtendedPatient } from '../../context';
import { PatientListForms } from '../Forms/PatientListForms';
import { StandAloneForm } from '../Forms/StandAloneForm';
import { CrossPlatform } from '../Forms';
import { QuestionnaireEngine } from '../QuestionnaireEngine';
import { questionnaireEngine } from '../Form/constants';
import IdgTabs from '../Tabs';
import NotFound from '../NotFound';

LicenseManager &&
  LicenseManager.setLicenseKey(process.env.REACT_APP_AG_GRID_KEY as string);

type BodyProps = {
  headerRef: React.RefObject<HTMLElement>;
  patient?: FormsExtendedPatient;
  formName: string | null;
  readOnly: boolean;
  smallWidget?: boolean;
  embedded?: boolean;
  showSpinner?: boolean;
  hideCheckmark?: boolean;
  setHideCheckmark?: (value: boolean) => void;
  patientError?: {
    status: number;
    statusText?: string;
    patientNumber?: string;
  };
};

const FormEdit = lazy(() => import('../Form/FormEdit'));
/**
 * !Please do not modify APP CONTENT CSS
 */
const AppContent = styled.div<{
  topBarHeight: number;
}>`
  height: 100%;
  margin-top: ${(props) => props.topBarHeight}px;
  width: 100%;
  @media print {
    height: auto;
  }
`;

/**
 * when clicking on a Form in ListForm the entire app reloads
 */

export const Body: React.SFC<BodyProps> = React.memo(
  ({
    patient,
    headerRef,
    formName,
    readOnly,
    embedded,
    showSpinner,
    hideCheckmark,
    patientError,
    setHideCheckmark,
  }) => {
    const topBarHeight = useComponentHeight(headerRef);

    const mainPaths = embedded
      ? ['/:patientNumber']
      : [
          '/:patientNumber/:formName/(form|changes)?/:submissionId?',
          '/:patientNumber/:formName/:submissionId?',
        ];

    const questionnaireEngineForm =
      patient && formName ? formName.includes(questionnaireEngine) : false;
    const fajitaEngineForm =
      patient && formName ? formName.includes(FAJITA) : false;

    return (
      <AppContent id="form-app-content" topBarHeight={topBarHeight}>
        <Switch>
          {patientError && (
            <Route path="/" render={(props: any) => <NotFound {...props} />} />
          )}
          {questionnaireEngineForm && (
            <QuestionnaireEngine
              formName={formName as string}
              topBarHeight={topBarHeight}
              showSpinner={showSpinner}
              hideCheckmark={hideCheckmark}
              setHideCheckmark={setHideCheckmark}
            />
          )}
          {fajitaEngineForm && <FajitaIntro />}
          {patient && formName ? (
            <Suspense fallback={<Loader contained />}>
              <Switch>
                <Route
                  path={mainPaths}
                  render={(props: any) => (
                    // NP-9689 LCD Recent Changes display overlaps- quick fix is to hide for 1.6 prod release
                    <IdgTabs
                      readOnly={readOnly || false}
                      formName={formName}
                      Form={
                        <FormEdit
                          Redirect={Redirect}
                          readOnly={readOnly}
                          {...props}
                        />
                      }
                      {...props}
                    />
                  )}
                />
              </Switch>
            </Suspense>
          ) : null}
          {patient ? (
            <Suspense fallback={<Loader contained />}>
              <Route
                path="/:patientNumber"
                render={(props: any) =>
                  embedded ? (
                    <PatientListForms embedded={embedded} {...props} />
                  ) : (
                    <StandAloneForm />
                  )
                }
              />
            </Suspense>
          ) : (
            <Suspense fallback={<Loader contained />}>
              <Route path="/" render={() => <CrossPlatform />} />
            </Suspense>
          )}
        </Switch>
      </AppContent>
    );
  },
);
